import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NotificationDropdown from "@/pages/admin/components/NotificationDropdown";

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface SidebarLink {
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebarLinks: SidebarLink[] = [
    { label: t("admin.dashboard"), href: "/admin", icon: "ri-dashboard-line" },
    { label: t("admin.products"), href: "/admin/products", icon: "ri-shopping-bag-3-line", badge: 156 },
    { label: t("admin.orders"), href: "/admin/orders", icon: "ri-file-list-3-line", badge: 1254 },
    { label: t("admin.customers"), href: "/admin/customers", icon: "ri-group-line", badge: 873 },
  ];

  return (
    <div className="min-h-screen bg-background-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 bg-background-50 border-r border-background-200/70 flex flex-col transition-all duration-300 ${
          sidebarCollapsed ? "w-[72px]" : "w-[260px]"
        }`}
      >
        {/* Logo area */}
        <div className="h-16 flex items-center px-5 border-b border-background-200/70 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-3 cursor-pointer overflow-hidden">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-heading font-bold text-sm">G</span>
            </div>
            {!sidebarCollapsed && (
              <span className="font-heading font-bold text-foreground-950 text-lg whitespace-nowrap">Galore</span>
            )}
          </Link>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {sidebarLinks.map((link) => {
            const isActive = location.pathname === link.href ||
              (link.href !== "/admin" && location.pathname.startsWith(link.href));
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70"
                }`}
              >
                <i className={`${link.icon} text-lg flex-shrink-0 ${isActive ? "text-primary-500" : ""}`}></i>
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1">{link.label}</span>
                    {link.badge !== undefined && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        isActive
                          ? "bg-primary-200 text-primary-800"
                          : "bg-background-200 text-foreground-500"
                      }`}>
                        {link.badge.toLocaleString()}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle + Store link */}
        <div className="px-3 py-4 border-t border-background-200/70 space-y-2 flex-shrink-0">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-900 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-2-line text-lg flex-shrink-0"></i>
            {!sidebarCollapsed && <span>{t("nav.home")}</span>}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground-400 hover:text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className={`text-lg flex-shrink-0 ${sidebarCollapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"}`}></i>
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? "ml-[72px]" : "ml-[260px]"}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 flex items-center justify-between px-6">
          <div>
            <h2 className="text-sm font-semibold text-foreground-700 font-heading">
              {sidebarLinks.find(l => {
                const isActive = location.pathname === l.href ||
                  (l.href !== "/admin" && location.pathname.startsWith(l.href));
                return isActive;
              })?.label || t("admin.dashboard")}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <NotificationDropdown />
            <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-foreground-600">
              <i className="ri-user-line text-sm"></i>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}